/**
 * Integration Display Name Utilities
 *
 * Provides standardized short and long display names for integrations.
 * Used across the application for consistent naming in tables, filters,
 * and detail pages.
 *
 * @module integrationDisplay.util
 */

/**
 * Manual overrides for integration short names.
 *
 * Comprehensive mapping of all 67 frontend integrations to their short display names.
 * Used in tables, filters, and other space-constrained UI elements.
 *
 * Key: Integration ID (lowercase, as stored in backend)
 * Value: Short display name (for tables, filters, compact spaces)
 */
export const SHORT_NAME_OVERRIDES: Record<string, string> = {
  // Cloud Providers
  amazon: 'AWS',
  azure: 'Azure',
  digitalocean: 'DigitalOcean',
  gcp: 'GCP',
  ibmcloud: 'IBM Cloud',

  // Security & MDR
  attackiq: 'AttackIQ',
  avalor: 'Avalor',
  burpsuite: 'Burp',
  cortex: 'Cortex XDR',
  crowdstrike: 'CrowdStrike',
  'crowdstrike-flightcontrol': 'CrowdStrike Flight Control',
  cymulate: 'Cymulate',
  invicti: 'Invicti',
  'microsoft-defender': 'Defender',
  orca: 'Orca',
  safebreach: 'SafeBreach',
  sentinelone: 'SentinelOne',
  wiz: 'Wiz',

  // Vulnerability Management
  insightvm: 'InsightVM',
  nessus: 'Nessus',
  nexpose: 'Nexpose',
  qualys: 'Qualys',
  tenablevm: 'Tenable',

  // Asset Management
  axonius: 'Axonius',
  censys_asm: 'Censys ASM',
  cycognito: 'CyCognito',
  flexera: 'Flexera',
  jupiterone: 'JupiterOne',
  runzero: 'RunZero',
  sevco: 'Sevco',
  sharphound: 'Sharphound',
  xpanse: 'Xpanse',
  armorcode: 'ArmorCode',

  // Identity & Access
  entraid: 'Entra ID',
  googlesso: 'Google SSO',
  gsuite: 'Google Suite',
  intune: 'Intune',
  okta: 'Okta',
  pingone: 'PingOne',
  sailpoint: 'SailPoint ISC',

  // SIEM
  splunk: 'Splunk',
  panther: 'Panther',

  // Secure Code Scanning
  checkmarx: 'Checkmarx',
  'snyk-apprisk': 'Snyk AppRisk',
  snyk_code: 'Snyk Code',
  'snyk-dast': 'Snyk DAST',

  // Web Application Hosting
  vercel: 'Vercel',

  // SCM & DevOps
  apiiro: 'Apiiro',
  bitbucket: 'Bitbucket',
  github: 'GitHub',
  gitlab: 'GitLab',
  legit: 'Legit',

  // Collaboration & Knowledge Management
  confluence: 'Confluence Cloud',

  // ITSM & Notifications
  email: 'Email',
  freshdesk: 'Freshdesk',
  freshservice: 'Freshservice',
  gchat: 'GChat',
  hook: 'Webhook',
  jira: 'Jira Cloud',
  servicenow: 'ServiceNow',
  slack: 'Slack',
  teams: 'Microsoft Teams',
  webhook: 'Outgoing Webhook',
  zulip: 'Zulip',

  // DNS & Network
  cloudflare: 'Cloudflare',
  dnsdb: 'dnsdb',
  fastly: 'Fastly',
  godaddy: 'GoDaddy',
  ns1: 'NS1',
  securitytrails: 'SecurityTrails',
  shodan: 'Shodan',

  // Firewall & WAF
  'akamai-waf': 'Akamai',
  awswaf: 'AWS WAF',
  'cloudflare-waf': 'Cloudflare',
  imperva: 'Imperva',
  palo: 'Panorama',
  threatx: 'ThreatX',

  // Threat Intelligence
  extrahop: 'ExtraHop',
  recordedfuture: 'Recorded Future',
  socradar: 'SOCRadar',

  // Import Utilities (Backend Only)
  'cisa-ch-vulnscan-import': 'CISA Cyber Hygiene',
  'cisa-ch-webappscan-import': 'CISA Cyber Hygiene',
  'insightvm-import': 'InsightVM',
  'nessus-import': 'Nessus',
  'nsa-csaas-asm-import': 'NSA ASM',
  'qualys-import': 'Qualys',
  'seed-import': 'Seed Import',
  'sharphound-import': 'Sharphound',
};

/**
 * Applies algorithmic shortening rules to integration titles.
 *
 * Removes verbose suffixes that don't add value in constrained spaces
 * like table cells and filter dropdowns. Preserves brand identity while
 * reducing visual clutter.
 *
 * Rules applied (in order):
 * 1. "Vulnerability Management" → "VM"
 * 2. Remove " Enterprise" suffix
 * 3. Remove " Platform" suffix
 * 4. Remove " Security" suffix
 * 5. Trim whitespace and normalize multiple spaces
 *
 * @param fullName - The full integration title from backend
 * @returns Shortened version of the name
 *
 * @example
 * applyShorteningAlgorithm('Qualys Vulnerability Management')
 * // Returns: 'Qualys VM'
 *
 * @example
 * applyShorteningAlgorithm('Burp Suite Enterprise')
 * // Returns: 'Burp Suite'
 */
export function applyShorteningAlgorithm(fullName: string): string {
  // First normalize whitespace to ensure consistent matching
  let result = fullName.trim().replace(/\s+/g, ' ');

  // Then apply suffix removal rules
  result = result
    .replace(/ Vulnerability Management$/i, ' VM')
    .replace(/ Enterprise$/i, '')
    .replace(/ Platform$/i, '')
    .replace(/ Security$/i, '');

  // Final trim in case we removed a suffix
  return result.trim();
}

/**
 * Get short display name for an integration.
 *
 * Used in space-constrained UI elements like table cells, filter dropdowns,
 * and compact card views. Prioritizes manual overrides for well-known
 * abbreviations, then applies algorithmic shortening.
 *
 * Priority order:
 * 1. Manual override from SHORT_NAME_OVERRIDES (for special cases)
 * 2. Algorithmic shortening (removes common verbose suffixes)
 * 3. Fallback to full name (if no shortening possible)
 *
 * @param fullName - The full integration title from backend capability
 * @param integrationId - The integration identifier (used for override lookup)
 * @returns Short display name suitable for compact spaces
 *
 * @example
 * getShortName('Burp Suite Enterprise', 'burpsuite')
 * // Returns: 'Burp' (from override)
 *
 * @example
 * getShortName('Qualys Vulnerability Management', 'qualys')
 * // Returns: 'Qualys VM' (from algorithm)
 *
 * @example
 * getShortName('GitHub', 'github')
 * // Returns: 'GitHub' (no shortening needed)
 */
export function getShortName(fullName: string, integrationId: string): string {
  if (!fullName || !integrationId) {
    return fullName || integrationId || '';
  }

  // Priority 1: Check override map (case-insensitive)
  const override = SHORT_NAME_OVERRIDES[integrationId.toLowerCase()];
  if (override) {
    return override;
  }

  // Priority 2: Apply algorithmic shortening
  const shortened = applyShorteningAlgorithm(fullName);

  return shortened || fullName;
}

/**
 * Get long display name for an integration.
 *
 * Used in detail pages and places where full context is needed.
 * Currently a passthrough function, but provides an extension point
 * for future enhancements (e.g., adding vendor names, formatting).
 *
 * @param fullName - The full integration title from backend capability
 * @returns Long display name suitable for detail views
 *
 * @example
 * getLongName('Burp Suite Enterprise')
 * // Returns: 'Burp Suite Enterprise'
 */
export function getLongName(fullName: string): string {
  return fullName || '';
}
